import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Event, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import {
  countChildrenByField,
  sidebarDebug,
  sidebarDebugEnabled,
  summarizeMenuNode,
} from './sidebar-debug';

export interface SidebarMenuNode {
  label?: string;
  routerLink?: string | string[];
  path?: string;
  url?: string;
  icon?: string;
  classs?: string;
  isExternalLink?: boolean;
  /** API thường trả `submenus`; Prime / mock dùng `children` hoặc `items` */
  children?: SidebarMenuNode[];
  items?: SidebarMenuNode[];
  submenus?: SidebarMenuNode[];
  menuId?: string | number;
  key?: string;
  id?: string | number;
  _key?: string;
}

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent implements OnInit, OnChanges {
  /** Logo mockup Figma (v11 asset) */
  readonly logoUrl =
    'https://wide-bounce-40173214.figma.site/_assets/v11/4a9cf86e13f0f5a9908e44a9d0f224dd88654a9a.png';

  @Input() menuItems: SidebarMenuNode[] = [];
  @Input() sidebarExpanded = true;
  @Output() toggleSidebar = new EventEmitter<void>();
  @Output() expandSidebar = new EventEmitter<void>();

  mainNavItems: SidebarMenuNode[] = [];
  footerNavItems: SidebarMenuNode[] = [];
  private readonly expandedKeys = new Set<string>();
  private pendingOpenKey: string | null = null;

  constructor(private router: Router) {
    this.router.events
      .pipe(filter((e: Event): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => {
        this.syncExpandedFromRoute();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['menuItems']) {
      const cur = changes['menuItems'].currentValue as SidebarMenuNode[] | undefined;
      const prevLen = changes['menuItems'].previousValue?.length;
      sidebarDebug('ngOnChanges menuItems', {
        previousLength: prevLen,
        currentLength: Array.isArray(cur) ? cur.length : 'not-array',
        firstInputSample: Array.isArray(cur) && cur[0] ? summarizeMenuNode(cur[0]) : null,
      });
      this.buildNavTree();
      this.syncExpandedFromRoute();
      this.logBuiltMenuState('after ngOnChanges');
    }
    if (changes['sidebarExpanded'] && this.sidebarExpanded && this.pendingOpenKey) {
      this.expandedKeys.add(this.pendingOpenKey);
      this.pendingOpenKey = null;
    }
  }

  ngOnInit(): void {
    sidebarDebug('ngOnInit', {
      inputMenuLength: this.menuItems?.length ?? 0,
      sidebarExpanded: this.sidebarExpanded,
    });
    this.buildNavTree();
    this.syncExpandedFromRoute();
    this.logBuiltMenuState('after ngOnInit');
  }

  onToggleClick(): void {
    this.toggleSidebar.emit();
  }

  getChildItems(item: SidebarMenuNode): SidebarMenuNode[] {
    const raw =
      item.children ?? item.items ?? item.submenus ?? [];
    return raw.filter((d) => !this.isClasssHidden(d.classs));
  }

  isLeaf(item: SidebarMenuNode): boolean {
    return (
      this.getChildItems(item).length === 0 && !!this.itemLink(item)
    );
  }

  isParent(item: SidebarMenuNode): boolean {
    return this.getChildItems(item).length > 0;
  }

  itemKey(item: SidebarMenuNode, index: number, zone: 'main' | 'footer'): string {
    const base = String(item.menuId ?? item.key ?? item.id ?? item.label ?? index);
    return zone === 'footer' ? `f-${base}` : `m-${base}`;
  }

  isSectionOpen(key: string): boolean {
    return this.expandedKeys.has(key);
  }

  onParentClick(item: SidebarMenuNode, key: string): void {
    if (!this.sidebarExpanded) {
      this.pendingOpenKey = key;
      this.expandSidebar.emit();
      return;
    }
    if (this.expandedKeys.has(key)) {
      this.expandedKeys.delete(key);
    } else {
      this.expandedKeys.add(key);
    }
  }

  currentPath(): string {
    return (this.router.url.split('?')[0] || '').trim();
  }

  /** Path matching aligned with web-hrm `isCurrentPage` */
  isCurrentPage(link: string | undefined): boolean {
    if (!link) {
      return false;
    }
    const normalizePath = (path: string): string =>
      path.startsWith('/') ? path : `/${path}`;
    const currentPath = normalizePath(this.currentPath());
    const targetPath = normalizePath(String(link));
    if (currentPath === targetPath) {
      return true;
    }
    if (currentPath.startsWith(targetPath)) {
      const nextChar = currentPath[targetPath.length];
      return !nextChar || nextChar === '/';
    }
    return false;
  }

  isLeafActive(item: SidebarMenuNode): boolean {
    const target = this.itemLink(item);
    if (target == null || target === '') {
      return false;
    }
    if (item.isExternalLink) {
      return window.location.pathname.includes(String(target));
    }
    return this.isCurrentPage(String(target));
  }

  isChildActive(child: SidebarMenuNode): boolean {
    const target = this.itemLink(child);
    if (target == null || target === '') {
      return false;
    }
    if (child.isExternalLink) {
      return window.location.pathname.includes(String(target));
    }
    return this.isCurrentPage(String(target));
  }

  isParentRowActive(item: SidebarMenuNode): boolean {
    const path = this.currentPath();
    if (
      item.path &&
      this.apiClassString(item.classs) === 'navigation-header' &&
      path.split('/').indexOf(String(item.path)) > -1
    ) {
      return true;
    }
    return this.getChildItems(item).some((c) => this.isChildActive(c));
  }

  navigateToItem(item: SidebarMenuNode): void {
    const target = this.itemLink(item);
    if (target == null || target === '') {
      return;
    }
    if (item.isExternalLink) {
      window.location.href = String(target);
      return;
    }
    if (Array.isArray(target)) {
      this.router.navigate(target);
      return;
    }
    const s = String(target);
    if (s.startsWith('/')) {
      void this.router.navigateByUrl(s);
      return;
    }
    void this.router.navigate([s]);
  }

  linkCommands(item: SidebarMenuNode): any[] {
    const rl = this.itemLink(item);
    if (rl == null || rl === '') {
      return ['/'];
    }
    if (Array.isArray(rl)) {
      return rl;
    }
    if (typeof rl === 'string' && rl.startsWith('/')) {
      const parts = rl.split('/').filter(Boolean);
      return parts.length ? ['/', ...parts] : ['/'];
    }
    return [rl];
  }

  trackByChild(_index: number, child: SidebarMenuNode): string | number {
    return child.id ?? child.key ?? child.menuId ?? child.label ?? _index;
  }

  /** Chuẩn hóa link từ các field API hay dùng */
  itemLink(item: SidebarMenuNode): string | string[] | undefined {
    return item.routerLink ?? item.path ?? item.url;
  }

  /**
   * API đôi khi trả `classs` / `icon` là object; NgClass chỉ chấp nhận string (hoặc object map boolean).
   */
  apiClassString(value: unknown): string {
    if (value == null) {
      return '';
    }
    if (typeof value === 'string') {
      return value;
    }
    return '';
  }

  /**
   * API: `classs` dạng "nav-item hidden" — có class `hidden` thì không hiển thị trên sidebar.
   */
  isClasssHidden(classs: unknown): boolean {
    const s = this.apiClassString(classs).trim();
    if (!s) {
      return false;
    }
    return s.split(/\s+/).some((token) => token === 'hidden');
  }

  /**
   * Angular 15 NgClass: nếu binding là mảng thì mỗi phần tử phải là string — không gộp `{ ... }` trong mảng.
   */
  sidebarNavButtonNgClass(
    item: SidebarMenuNode,
    row: 'leaf' | 'parent'
  ): Record<string, boolean> {
    const out: Record<string, boolean> = {};
    for (const token of this.apiClassString(item.classs).split(/\s+/).filter(Boolean)) {
      out[token] = true;
    }
    out['gap-3 px-3'] = this.sidebarExpanded;
    out['justify-center px-0'] = !this.sidebarExpanded;
    if (row === 'leaf') {
      out['text-primary'] = this.isLeafActive(item);
      out['text-foreground hover:bg-secondary'] = !this.isLeafActive(item);
    } else {
      out['text-primary'] = this.isParentRowActive(item);
      out['text-foreground hover:bg-secondary'] = !this.isParentRowActive(item);
    }
    return out;
  }

  /** Nền hàng khi active — dùng token mockup sidebar */
  navLeafRowBg(item: SidebarMenuNode): string {
    return this.isLeafActive(item) ? 'var(--sidebar-item-active-bg)' : 'transparent';
  }

  navParentRowBg(item: SidebarMenuNode): string {
    return this.isParentRowActive(item) ? 'var(--sidebar-item-active-bg)' : 'transparent';
  }

  childRowBg(child: SidebarMenuNode): string {
    return this.isChildActive(child)
      ? 'var(--sidebar-child-item-active-bg)'
      : 'transparent';
  }

  /** NgClass cho dòng con — chỉ string keys (Angular 15) */
  childRowNgClass(child: SidebarMenuNode): Record<string, boolean> {
    const out: Record<string, boolean> = {};
    for (const token of this.apiClassString(child.classs).split(/\s+/).filter(Boolean)) {
      out[token] = true;
    }
    out['text-primary'] = this.isChildActive(child);
    out['text-foreground'] = !this.isChildActive(child);
    out['sidebar-shell-child--active'] = this.isChildActive(child);
    return out;
  }

  /**
   * Giữ nguyên thứ tự root từ API. (Trước đây phần tử cuối bị tách sang footer → neo đáy sidebar, sai với dữ liệu động.)
   */
  private buildNavTree(): void {
    const list = (this.menuItems ?? []).filter((x) => !this.isClasssHidden(x.classs));
    this.mainNavItems = list.map((x, i) => ({
      ...x,
      _key: this.itemKey(x, i, 'main'),
    }));
    this.footerNavItems = [];
  }

  private allRootItems(): SidebarMenuNode[] {
    return [...this.mainNavItems, ...this.footerNavItems];
  }

  private syncExpandedFromRoute(): void {
    this.allRootItems().forEach((item) => {
      const key = item._key;
      if (!key) {
        return;
      }
      if (this.getChildItems(item).some((c) => this.isChildActive(c))) {
        this.expandedKeys.add(key);
      }
      if (
        item.path &&
        this.currentPath().split('/').indexOf(String(item.path)) > -1 &&
        this.apiClassString(item.classs) === 'navigation-header'
      ) {
        this.expandedKeys.add(key);
      }
    });
  }

  /**
   * Giải thích nhanh vì sao từng root có/không khớp template (*ngIf).
   * Chỉ chạy khi localStorage DEBUG_SIDEBAR=1
   */
  private logBuiltMenuState(where: string): void {
    if (!sidebarDebugEnabled()) {
      return;
    }
    const rows: Record<string, unknown>[] = [];
    for (const item of this.allRootItems()) {
      const childCount = this.getChildItems(item).length;
      const counts = countChildrenByField(item);
      const link = this.itemLink(item);
      const tplNavHeader =
        this.apiClassString(item.classs) !== 'navigation-header' ||
        this.isParent(item);
      rows.push({
        label: item.label,
        classs: item.classs,
        classsString: this.apiClassString(item.classs),
        childCountsRaw: counts,
        visibleChildCount: childCount,
        itemLink: link ?? '(none)',
        isLeaf: this.isLeaf(item),
        isParent: this.isParent(item),
        passesTemplateGate: tplNavHeader,
        wouldRenderLeaf: tplNavHeader && this.isLeaf(item),
        wouldRenderParent: tplNavHeader && this.isParent(item),
        wouldRenderNothing:
          tplNavHeader && !this.isLeaf(item) && !this.isParent(item),
      });
    }
    sidebarDebug(`built menu state (${where})`, {
      url: this.currentPath(),
      mainCount: this.mainNavItems.length,
      footerCount: this.footerNavItems.length,
      navVisible: this.mainNavItems.length > 0 || this.footerNavItems.length > 0,
      rows,
    });
  }
}
